import { giphyDarkestGrey, giphyRed } from '@giphy/colors'
import { isEqual, omit } from 'lodash'
import { useContext, useState } from 'react'
import useAsyncEffect from 'shared/hooks/use-async-effect'
import styled from 'styled-components'
import { BORDER_RADIUS_MEDIUM, SUPER_CUT_MIME_TYPES, UGE_GA_PREFIX } from '../../../constants'
import { CreationToolsContext } from '../../../manager'
import { CreationToolSourceFile } from '../../../types'
import CreationTool from '../../../types/creation-tool'
import CreationToolOutputType from '../../../types/output-type'
import CreationToolWatermarkPosition from '../../../types/watermark'
import { validateSourceByInput } from '../../../utils/validate-source'
import AlertModal from '../../alert-modal'
import { ToolButton } from '../../buttons'
import SourceEditor from '../../source-editor'
import { SourceEditorEdits } from '../../source-editor/types'
import { ICapture, IEditParams, ISegment } from '../shared/types'
import BaseModal from './base'

const Modal = styled(BaseModal)`
    align-items: center;
    display: flex;
    justify-content: center;
    margin: 0 auto;
    width: 1040px;
`

const EditorWrapper = styled.div`
    display: flex;
    flex: 1;
    margin-top: 75px;
    min-height: calc(100vh - 75px);
`

const CloseButton = styled(ToolButton)`
    background: ${giphyDarkestGrey};
    border-radius: ${BORDER_RADIUS_MEDIUM}px;
    color: #fff;
    font-size: 20px;
    height: 50px;
    line-height: 54px;
    text-align: center;
    width: 50px;
`

type Props = {
    capture: ICapture
    segment: ISegment
    onClose: () => void
    onSave: (segment: ISegment) => void
}

const CreationToolsEditSegmentModal = ({ capture, segment, onClose, onSave }: Props) => {
    const { trackEvent, tool } = useContext(CreationToolsContext)
    const [validatedSource, setValidatedSource] = useState<CreationToolSourceFile | undefined>()
    const [hasEdits, setHasEdits] = useState<boolean>(false)
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false)
    const gaCategory = CreationTool.gaEventCategory(tool)

    const defaultEdits = segment.editParams
        ? omit(segment.editParams, ['isMerged', 'displayOrder', 'displayHidden', 'watermark']) // remove props that don't exist on SourceEditorEdits type
        : undefined

    const fireGAEvent = (action: string, value?: string) => {
        trackEvent(gaCategory, `${UGE_GA_PREFIX}${action}`, capture.id, value)
    }

    useAsyncEffect(async () => {
        fireGAEvent('Edit')
        setValidatedSource(undefined)
        const validated = await validateSourceByInput(segment.video_url, SUPER_CUT_MIME_TYPES)
        setValidatedSource(validated)
    }, [segment])

    const onEditorUpdate = (edits: SourceEditorEdits) => {
        setHasEdits(!isEqual(edits, defaultEdits))
    }

    const onCloseClick = () => {
        if (hasEdits) {
            setShowConfirmModal(true)
        } else {
            onClose()
        }
    }

    const onDone = (edits?: SourceEditorEdits) => {
        const editParams: IEditParams | undefined = edits
            ? {
                  ...omit(edits, 'frames'),
                  displayOrder: segment.index,
                  displayHidden: false,
                  isMerged: false,
              }
            : undefined

        // caption watermark
        if (edits?.frames && editParams?.captions) {
            editParams.watermark = { id: '', filename: '', imageUrl: edits?.frames[0].canvas.toDataURL('image/png') }
        }

        // caption events
        if (edits?.captions && !segment.editParams?.captions) {
            const totalCaptions = edits.captions.length
            fireGAEvent('Add Caption', `${totalCaptions}`)
        }

        // crop events
        if (edits?.crop && !segment.editParams?.crop) {
            const aspectRatio = (edits.crop.width / edits.crop.height).toFixed(2)
            fireGAEvent('Crop', aspectRatio)
        }

        // trim events
        if (edits?.trim && !segment.editParams?.trim) {
            const duration = Math.round(edits.trim.end - edits.trim.start)
            fireGAEvent('Trim', `${duration}`)
        }

        onSave({ ...segment, editParams })
    }

    return (
        <Modal isLoading={!validatedSource} onClose={onCloseClick}>
            {validatedSource && (
                <EditorWrapper style={showConfirmModal ? { display: 'none' } : {}}>
                    <SourceEditor
                        {...{
                            source: validatedSource,
                            edits: defaultEdits,
                            outputType: CreationToolOutputType.Video,
                            customFont: capture.custom_font,
                            onUpdate: onEditorUpdate,
                            onDone,
                            watermark: capture.watermark
                                ? {
                                      url: capture.watermark.imageUrl,
                                      position:
                                          capture.upload_metadata?.watermark_position ||
                                          CreationToolWatermarkPosition.BottomRight,
                                      width:
                                          parseInt(capture.upload_metadata?.watermark_width || '0') *
                                          (segment.width / capture.width),
                                  }
                                : undefined,
                        }}
                    >
                        <CloseButton className="ss-delete" onClick={onCloseClick} />
                    </SourceEditor>
                </EditorWrapper>
            )}
            {showConfirmModal && (
                <AlertModal
                    {...{
                        stickerId: 'jOsoGmmWGSloPU8fMH',
                        title: 'You will lose your changes!',
                        primaryButtonLabel: 'Discard Changes',
                        primaryButtonColor: giphyRed,
                        secondaryButtonLabel: 'Nevermind',
                        onPrimaryButtonClick: onClose,
                        onSecondaryButtonClick: () => setShowConfirmModal(false),
                    }}
                />
            )}
        </Modal>
    )
}

export default CreationToolsEditSegmentModal
